
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, input, Signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TrackLoginRequestParams } from '@app/models/tracking/track-event-param-type';
import { ButtonComponent } from '@commonComponents/button/button.component';
import { LoadingComponent } from '@commonComponents/loading/loading.component';
import { delayHelper } from '@commonHelpers/delay-helper';
import { isNullish } from '@commonHelpers/math-utils';
import { NgbDropdown, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { environment } from '@environments/environment';
import { LoginSource } from "@interfaces/HttpClient/AnalyticsApiTrackingModels";
import { AccountService } from '@services/account.service';
import { AnalyticsTrackingFacadeService } from '@services/analytics-tracking-facade.service';
import { ApplicationInsightsService } from '@services/application-insights.service';
import { PopupService } from '@services/popup.service';
import { AuthState, UserAuthorizationService } from '@services/user-authorization.service';
import { timer } from 'rxjs';
import { UserMenuComponent } from '../user-menu.component';
;

@Component({
	// Eager ist hier notwendig, nicht Bestandsschutz: das Template rendert `{{ initials }}` aus
	// einem Getter, der `accountService.getAccount()` liest. Der MSAL-Account wird erst
	// asynchron nach dem Anmelden verfügbar, und ein Getter schreibt nichts, was Angular
	// beobachten könnte - unter OnPush friert die Anzeige auf dem Stand des letzten Checks ein
	// und das Kontomenü zeigt einen leeren grauen Kreis. Die beiden detectChanges() in dieser
	// Klasse decken die Klickpfade ab, nicht den Moment, in dem MSAL den Account auflöst.
	// markForCheck() wäre kein Ersatz: die Komponente beobachtet den Kontowechsel gar nicht.
	changeDetection: ChangeDetectionStrategy.Eager,
	selector: 'app-user-menu-context',
	templateUrl: './user-menu-context.component.html',
	styleUrls: ['./user-menu-context.component.scss'],
	imports: [NgbDropdown, NgbDropdownToggle, LoadingComponent, NgbDropdownMenu, ButtonComponent, UserMenuComponent, TranslatePipe]
})
export class UserMenuContextComponent {

	constructor(
		private accountService: AccountService,
		private userAuthorizationService: UserAuthorizationService,
		private aiService: ApplicationInsightsService,
		private trackingFacadeService: AnalyticsTrackingFacadeService,
		private cdRef: ChangeDetectorRef,
		private popupService: PopupService
	) {
		this.authState = this.userAuthorizationService.authState;

		// Zweiter, späterer Auslöser für die Anzeige der Initialen.
		//
		// `{{ initials }}` kommt aus einem Getter, der `accountService.getAccount()` liest. Das
		// Template liest zwar das Signal `authState`, aber dessen Wert wird in handleLogin()
		// gesetzt, bevor der Kontostand vollständig ist - und danach ändert es sich nicht mehr.
		// Fällt die Auswertung des Getters in dieses Fenster, bleibt der Kreis leer.
		//
		// `Eager` an dieser Komponente hilft dagegen nicht, obwohl sie es trägt: im mobilen Kopf
		// ist der Elternteil `mobile-header` OnPush, und ein nicht dirty OnPush-Elternteil wird
		// samt Teilbaum übersprungen - das Eager-Kind wird also nie erreicht. Auf dem Desktop
		// tritt es nicht auf, weil `header` selbst Eager ist. Verschärft hat es ngx-translate 18:
		// bis v17 markierte die translate-Pipe die Host-View als dirty und sorgte damit beiläufig
		// für den Durchlauf, ab v18 zieht sie nur noch ihren eigenen Wert nach.
		//
		// refreshMenu feuert in handleLogin() nach zwei awaits und nach persistLoginHint(), also
		// zu einem Zeitpunkt, an dem der Konto­stand sicher steht. markForCheck() markiert die
		// eigene View und alle Vorfahren - damit wird der Elternteil durchlaufen und der Getter
		// neu ausgewertet.
		this.userAuthorizationService.refreshMenu
			.pipe(takeUntilDestroyed())
			.subscribe(() => this.cdRef.markForCheck());
	}

	public readonly catalogKey = input<string>();
	public readonly target = input<string>();
	public readonly rel = input<string>();
	public readonly hover = input<boolean>();

	@ViewChild("userMenuDropdownNotLoggendIn") public userMenuDropdownNotLoggendIn: NgbDropdown;
	public readonly authState: Signal<AuthState>;
	private isHoveredOverMenu = false;

	public onHoverDropdown() {
		this.userMenuDropdownNotLoggendIn.open();
	}

	public onLeaveDropdown() {
		this.userMenuDropdownNotLoggendIn.close()
	}

	public leaveDropdownButton(event: MouseEvent) {
		this.isHoveredOverMenu = false;
		const domRect = (event.target as HTMLElement)?.getBoundingClientRect();
		if (!isNullish(domRect) && domRect.y + domRect.height > event.clientY) {
			this.onLeaveDropdown();
		} else {
			timer(300).subscribe(() => {
				if (!this.isHoveredOverMenu) {
					this.onLeaveDropdown();
				}
			})
		}

	}

	public userMenuChanged() {
		this.cdRef.detectChanges();
	}

	public async login(): Promise<void> {
		this.userAuthorizationService.clearData();

		this.trackingFacadeService.handleTrackEvent(new TrackLoginRequestParams(LoginSource.UserMenu, this.catalogKey()))
		await this.trackingFacadeService.commitAllTrackEvents();
		this.cdRef.detectChanges();
		delayHelper(async () => {
			await this.accountService.login();
		});

	}

	public async logout(): Promise<void> {
		this.userAuthorizationService.clearData();
		this.aiService.clearAuthenticatedUserContext();
		await this.accountService.logout();

	}

	public hoverCheck() {
		this.isHoveredOverMenu = true;
	}

	public openLoginProfits() {
		this.popupService.openLoginProfits(this.catalogKey());
		this.userMenuDropdownNotLoggendIn.close()
	}

	public hasSignup(): boolean {
		return environment.msal.signUpFlow !== "";
	}

	public async signup(): Promise<void> {
		this.userAuthorizationService.clearData()
		await this.accountService.signup();
		// JP 07.10.2021 Für Signup kein explizites Tracking erforderlich
	}

	public get initials() {
		const account = this.accountService.getAccount();
		const claims = account?.idTokenClaims as any;
		return (claims?.given_name?.[0] ?? '') + (claims?.family_name?.[0] ?? '')?.toUpperCase();
	}

}
